/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { message } from 'antd'
import login, { LoginBody } from 'api/auth/login'
import { AdminUser } from 'types'
import localStorage from 'utils/localStorage'

/**
 * Types for the slice of auth in the store
 */
export interface AuthState {
	isAuthenticated: boolean
	user: AdminUser | null
	loading: boolean
}

/**
 * Initial state of the slice auth
 */
const initialState: AuthState = {
	user: localStorage.get('user') || null,
	isAuthenticated: localStorage.get('isAuthenticated') || false,
	loading: false,
}

/**
 * Action for logging in the Admin User
 *
 * Tries to login the user and throws an error if it fails.
 *
 * @param {LoginBody} body body of the request to login
 * @returns {Promise<AdminUser>} The promise of the request to login
 * @throws If the request to login fails
 */

export const loginAction = createAsyncThunk<AdminUser, LoginBody>(
	'auth/loginAction',
	async (body, { rejectWithValue }) => {
		try {
			const response = await login(body)
			const { user } = response
			return user
		} catch (error: any) {
			return rejectWithValue(error.message)
		}
	}
)

export const authSlice = createSlice({
	name: 'auth',
	initialState,
	reducers: {
		logoutAction: state => {
			state.isAuthenticated = false
			state.user = null
			state.loading = false
			localStorage.clear()
		},
	},
	extraReducers: builder => {
		builder.addCase(loginAction.pending, state => {
			state.loading = true
		})
		builder.addCase(loginAction.fulfilled, (state, action) => {
			state.isAuthenticated = true
			state.user = action.payload
			state.loading = false
			message.success('Login successful')
			localStorage.set('isAuthenticated', true)
		})
		builder.addCase(loginAction.rejected, state => {
			state.loading = false
		})
	},
})

export const { logoutAction } = authSlice.actions

export default authSlice.reducer
